<script>
import { getWorkShareList } from '@/api/workShare.js'

export default {
  name: 'WorkShareListPage',
  data() {
    return {
      loading: false,
      filters: {
        keyword: '',
        postType: '',
        priorityType: '',
        workStatus: '',
      },
      page: 1,
      size: 10,
      total: 0,
      items: [],
      postTypes: [
        { title: '전체', value: '' },
        { title: '일반', value: 'GENERAL' },
        { title: '공지', value: 'NOTICE' },
        { title: '요청', value: 'REQUEST' },
        { title: '이슈', value: 'ISSUE' },
        { title: '정산', value: 'SETTLEMENT' },
        { title: '배송', value: 'DELIVERY' },
        { title: '상품', value: 'PRODUCT' },
        { title: '회원', value: 'MEMBER' },
        { title: '기타', value: 'ETC' },
      ],
      priorities: [
        { title: '전체', value: '' },
        { title: '보통', value: 'NORMAL' },
        { title: '중요', value: 'IMPORTANT' },
        { title: '긴급', value: 'URGENT' },
      ],
      statuses: [
        { title: '전체', value: '' },
        { title: '대기', value: 'OPEN' },
        { title: '확인', value: 'CONFIRMED' },
        { title: '진행중', value: 'IN_PROGRESS' },
        { title: '완료', value: 'DONE' },
        { title: '보류', value: 'HOLD' },
      ],
    }
  },
  mounted() {
    this.load(1)
  },
  methods: {
    async load(nextPage = this.page) {
      this.loading = true
      this.page = nextPage

      try {
        const res = await getWorkShareList({
          ...this.filters,
          page: this.page,
          size: this.size,
        })

        if (res.data?.code === 0) {
          this.items = res.data.data?.items || []
          this.total = res.data.data?.total || 0
        } else {
          this.items = []
          this.total = 0
        }
      } finally {
        this.loading = false
      }
    },
    reset() {
      this.filters = {
        keyword: '',
        postType: '',
        priorityType: '',
        workStatus: '',
      }
      this.load(1)
    },
    formatDt(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').substring(0, 16)
    },
    findTitle(items, value) {
      return items.find(item => item.value === value)?.title || value || '-'
    },
    postTypeLabel(value) {
      return this.findTitle(this.postTypes, value)
    },
    priorityLabel(value) {
      return this.findTitle(this.priorities, value)
    },
    statusLabel(value) {
      return this.findTitle(this.statuses, value)
    },
    priorityColor(value) {
      if (value === 'URGENT') return 'error'
      if (value === 'IMPORTANT') return 'warning'
      return 'secondary'
    },
    statusColor(value) {
      if (value === 'DONE') return 'success'
      if (value === 'CONFIRMED') return 'primary'
      if (value === 'IN_PROGRESS') return 'info'
      if (value === 'HOLD') return 'grey'
      return 'secondary'
    },
    statusSummary(row) {
      const parts = [
        { label: '대기', value: row.openCount || 0, color: 'secondary' },
        { label: '확인', value: row.confirmedCount || 0, color: 'primary' },
        { label: '완료', value: row.doneCount || 0, color: 'success' },
        { label: '보류', value: row.holdCount || 0, color: 'grey' },
      ].filter(item => item.value > 0)

      if (parts.length) return parts
      return [{ label: this.statusLabel(row.workStatus), value: null, color: this.statusColor(row.workStatus) }]
    },
    goDetail(row) {
      this.$router.push(`/work-share/${row.workSharePostCode}`)
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between flex-wrap ga-2 mb-4">
      <div>
        <h1 class="text-h5 font-weight-bold mb-1">업무공유게시판</h1>
        <div class="text-body-2 text-grey">본사와 지점 관리자 간 업무 요청과 공유 사항을 관리합니다.</div>
      </div>
      <v-btn color="primary" prepend-icon="mdi-plus" to="/work-share/new">게시글 등록</v-btn>
    </div>

    <v-card rounded="lg" elevation="1" class="mb-4">
      <v-card-title class="text-subtitle-2 font-weight-bold">검색 조건</v-card-title>
      <v-card-text>
        <div class="work-share-search">
          <v-text-field
            v-model="filters.keyword"
            placeholder="제목, 내용, 작성자 검색"
            variant="outlined"
            density="compact"
            hide-details
            @keyup.enter="load(1)"
          />
          <v-select
            v-model="filters.postType"
            :items="postTypes"
            label="유형"
            variant="outlined"
            density="compact"
            hide-details
          />
          <v-select
            v-model="filters.priorityType"
            :items="priorities"
            label="중요도"
            variant="outlined"
            density="compact"
            hide-details
          />
          <v-select
            v-model="filters.workStatus"
            :items="statuses"
            label="상태"
            variant="outlined"
            density="compact"
            hide-details
          />
          <div class="d-flex ga-2 justify-end">
            <v-btn variant="tonal" prepend-icon="mdi-refresh" @click="reset">초기화</v-btn>
            <v-btn color="primary" prepend-icon="mdi-magnify" @click="load(1)">검색</v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-card rounded="lg" elevation="1">
      <v-card-title class="d-flex align-center ga-2 text-subtitle-1 font-weight-bold">
        게시글 목록
        <v-chip size="small" variant="tonal">{{ total }}건</v-chip>
      </v-card-title>

      <v-table class="work-share-table">
        <thead>
          <tr>
            <th>유형</th>
            <th>중요도</th>
            <th>상태</th>
            <th>제목</th>
            <th>대상</th>
            <th>작성자</th>
            <th>종료기한</th>
            <th>작성일</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in items" :key="row.workSharePostCode" class="work-share-row" @click="goDetail(row)">
            <td>{{ postTypeLabel(row.postType) }}</td>
            <td>
              <v-chip size="small" :color="priorityColor(row.priorityType)" variant="tonal">
                {{ priorityLabel(row.priorityType) }}
              </v-chip>
            </td>
            <td>
              <div class="status-summary">
                <v-chip
                  v-for="item in statusSummary(row)"
                  :key="`${row.workSharePostCode}-${item.label}`"
                  size="small"
                  :color="item.color"
                  variant="tonal"
                >
                  {{ item.value === null ? item.label : `${item.label} ${item.value}` }}
                </v-chip>
              </div>
            </td>
            <td>
              <div class="font-weight-bold">{{ row.boardTitle }}</div>
              <div class="text-caption text-grey">
                댓글 {{ row.commentCount || 0 }} · 첨부 {{ row.attachmentCount || 0 }}
              </div>
            </td>
            <td class="text-caption">
              <div>{{ row.targetBranchNames || '전체 지점' }}</div>
              <div v-if="row.targetAdminNames">{{ row.targetAdminNames }}</div>
            </td>
            <td>{{ row.writerName || '-' }}</td>
            <td>{{ formatDt(row.dueDt) }}</td>
            <td>{{ formatDt(row.createdDt) }}</td>
          </tr>
          <tr v-if="!items.length">
            <td colspan="8" class="text-center text-grey py-8">
              등록된 업무공유 게시글이 없습니다.
            </td>
          </tr>
        </tbody>
      </v-table>

      <v-card-actions class="justify-space-between">
        <div class="text-caption text-grey">전체 {{ total }}건 중 {{ items.length }}건 표시</div>
        <v-pagination
          v-model="page"
          :length="Math.max(1, Math.ceil(total / size))"
          density="comfortable"
          @update:model-value="load"
        />
      </v-card-actions>
    </v-card>
  </div>
</template>

<style scoped>
.work-share-search {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) 160px 160px 160px 180px;
  gap: 12px;
  align-items: center;
}

.work-share-row {
  cursor: pointer;
}

.work-share-row:hover {
  background: rgba(211, 178, 105, 0.08);
}

.status-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.work-share-table :deep(th),
.work-share-table :deep(td) {
  color: #e8eef8;
}

@media (max-width: 1100px) {
  .work-share-search {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
